'use client';

import { getItemByProduct, updateCartItemQuantityAndPrices } from '../../helpers/cart-item.helpers';
import { type CartState } from './cart.reducer';
import {
    useCreateEmptyCartMutation,
    type CartItemInterface,
    type Money,
    type CreateEmptyCartMutationHookResult,
} from '../../types/front.graphql';
import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
    type PropsWithChildren,
} from 'react';
import { observer } from '@legendapp/state/react';
import { cartComputed, cartTransientStore$ } from '../cart.store';
import { cartStoreActions } from '../cart-store.actions';
import { useCartId } from '../../api';
import { authStore$ } from '@kidslabs/module-auth';
import { COULD_NOT_FIND_CART_MESSAGE, INACTIVE_CART_MESSAGE } from '../../constants/error-message.constants';
import { cartActionsEmitter } from '../../contexts';
// Import directly from individual files (NOT from ../../hooks barrel) to avoid circular dependency.
// The hooks barrel re-exports cart-mutation.hook.ts which imports useCart from this file.
import { useCartItemReference } from '../../hooks/cart-item-reference/use-cart-item-reference.hook';
import { useQuoteModuleLogout } from '../../hooks/use-quote-module-logout';
import { useQuoteStoreCodeChange } from '../../hooks/use-quote-store-code-change';
// Import useAssignCustomerToGuestCart directly from api/requests (NOT from ../../api barrel) to avoid circular dependency.
import { useAssignCustomerToGuestCart } from '../../api/requests/cart.request';
import { fetchCartSyncReferences, reconcileWithServerReferences } from '../../services/cart-sync-references.service';
import { cartItemReferenceStoreAPI } from '../cart-item-reference-store.public-api';
import { cartSessionManager } from '../../contracts';
import { storeCodeEmitter, logSentry } from '@kidslabs/module-core';
import { useSession } from 'next-auth/react';
import {
    setInternalUpdate,
    updateLastSyncedState,
    hasPendingQuantityChange,
    getPendingQuantity,
    getCartItemReferenceSyncStatus,
    forceCartItemReferenceSync,
    unlockSync,
} from '../cart-item-reference.store';
import { CartItemReferenceSynchronizationService } from '../../services/cart-item-reference-synchronization.service';
import type { CartItemReference } from '../../models';

// Debounce interval for proactive customer cart validation (ms)
const CART_VALIDATION_DEBOUNCE_MS = 30_000;

// ============================================================================
// CLEAN CART PROVIDER STATE: CartState + convenience methods
// ============================================================================

export interface CartProviderState extends CartState {
    // === CONVENIENCE METHODS ===
    doResetCurrentCart: () => void;
    getIdFromCart: () => string;
    setCartLoading: (isLoading?: boolean) => void;
    addOrIncreaseItemToCart: (item: CartItemInterface, quantity: number) => void;
    decreaseItemInCart: (id: CartItemInterface['id']) => void;
    getItemFromCart: (item: CartItemInterface | string) => CartItemInterface | undefined;
    isInCart: (item: CartItemInterface | string) => boolean;
    doSetCartId: (id: string) => void;
    setCartItems: (items: CartItemInterface[]) => void;
    cartTotal: () => Money;
    cartTotalItems: () => number;
    clearItemFromCart: (id: CartItemInterface['id']) => void;
    setCartIsFetching: (isLoading?: boolean) => void;
    fetchCartItem: (...args: unknown[]) => Promise<void>;
    updateItemInCart: (id: CartItemInterface['id'], quantity: number) => void;
    quantitiesOfProductInCart: (productId: number) => number;
    getCartItemBySku: (sku: string) => CartItemInterface | undefined;

    // New computed properties methods
    getTotalUniqueItems: () => number;
    getTotalValue: () => Money;
    isCartEmpty: () => boolean;
    isAnyLoading: () => boolean;

    // New specific loading state methods
    isCartActionsLoading: () => boolean;
    isCartFetchingLoading: () => boolean;
    isCartSetupLoading: () => boolean;
    setCartSetupLoading: (isLoading?: boolean) => void;

    // === MISSING METHODS - Now added to interface ===
    addItemsToCart: (items: CartItemInterface[]) => void;

    // Cart initialization state — downstream consumers (e.g., CartScreen)
    // MUST wait for this to be true before fetching cart data.
    isCartInitialized: () => boolean;

    // Proactive cart validation — call before critical operations (add-to-cart, checkout)
    // to ensure local cart ID matches the server's current customer cart.
    validateCartBeforeOperation: () => Promise<void>;

    // Legacy compatibility
    createEmptyCart: CreateEmptyCartMutationHookResult[0];
    updateCartLanguage: (language: string) => void;
}

export const WebCartContext = createContext<CartProviderState | undefined>(undefined);

WebCartContext.displayName = 'WebCartContext';

export const useCart = () => {
    const context = useContext(WebCartContext);
    if (context === undefined) {
        throw new Error(`useCart must be used within a CartProvider`);
    }
    return useMemo(() => context, [context]);
};

export const CartProvider = observer(({ children }: PropsWithChildren<NonNullable<unknown>>) => {
    // ========================================================================
    // MODULE LIFECYCLE HOOKS - Logout and store code change handlers
    // ========================================================================

    // Register logout handler for quote module (clears cart on logout)
    useQuoteModuleLogout();

    // Subscribe to store code changes (clears cart when store changes)
    useQuoteStoreCodeChange();

    // ========================================================================
    // DATA ACCESS LAYER - Clean separation of server data and local state
    // ========================================================================

    // Server data (controlled through cartActions - no direct store access)
    const cartId = cartStoreActions.getCartIdObservable().get();
    const cartState = cartStoreActions.getCartState();

    // Auth state — derived from next-auth session (source of truth)
    const { status: sessionStatus } = useSession();
    const authorized = sessionStatus === 'authenticated';

    // ========================================================================
    // LOCAL SESSION STATE - Legend-State observables for store code change
    // ========================================================================

    // These live in cartTransientStore$ so they are automatically cleared when
    // resetCartForStoreChange() is called (store code change only).
    // NOT cleared by resetCart() — clearing on logout would cause infinite loop:
    // resetCart → customerCartLoaded=false → auth effect re-runs → fetch → resetCart → loop
    const customerCartLoaded = cartTransientStore$.customerCartLoaded.get();
    const cartInitialized = cartTransientStore$.cartInitialized.get();

    const setCustomerCartLoaded = useCallback((value: boolean) => {
        cartTransientStore$.customerCartLoaded.set(value);
    }, []);
    const setCartInitialized = useCallback((value: boolean) => {
        cartTransientStore$.cartInitialized.set(value);
    }, []);

    // cartItemsLoaded is only used as a signal, keep as local state
    const [, setCartItemsLoaded] = useState(false);

    // Race condition prevention flags
    const isProcessingAuthFlow = useRef(false);
    const isFetchingCartItems = useRef(false);
    const isInitialized = useRef(false);
    const isSessionReconciled = useRef(false);
    const reconciliationPromise = useRef<Promise<string> | null>(null);

    // Track cartId that was used during the last fetch attempt.
    // If cartId changes while a fetch is in progress (e.g., IndexedDB hydration),
    // we need to re-fetch after the current fetch completes.
    const lastFetchedCartIdRef = useRef<string>('');

    // Debounce guard for proactive customer cart validation
    const lastCartValidationTime = useRef(0);

    // ========================================================================
    // STORE CODE CHANGE: Reset ALL refs when cartInitialized transitions to false
    // ========================================================================
    // When store code changes, resetCartForStoreChange() sets cartInitialized=false.
    // The observer re-renders, but useRef values persist across re-renders.
    // We must explicitly reset refs so the auth/guest flow can re-execute cleanly.
    // Track previous cartInitialized to detect the transition.
    const previousCartInitializedRef = useRef(cartInitialized);
    useEffect(() => {
        if (previousCartInitializedRef.current && !cartInitialized) {
            isProcessingAuthFlow.current = false;
            isFetchingCartItems.current = false;
            isSessionReconciled.current = false;
            reconciliationPromise.current = null;
            lastFetchedCartIdRef.current = '';
            lastCartValidationTime.current = 0;
            // NOTE: isInitialized is NOT reset — cart store init only needs to run once per mount.
        }
        previousCartInitializedRef.current = cartInitialized;
    }, [cartInitialized]);

    // ========================================================================
    // EXTERNAL DEPENDENCIES - APIs and hooks
    // ========================================================================

    const { getCustomerCartQuery } = useCartId();
    const [createEmptyCartMutation] = useCreateEmptyCartMutation();
    const { assignCustomerToGuestCart } = useAssignCustomerToGuestCart();

    const { setCartItemReferences } = useCartItemReference();

    // ========================================================================
    // BUSINESS LOGIC FUNCTIONS - Keep existing working logic
    // ========================================================================

    const setCartLoading = useCallback((isLoading = false) => {
        cartStoreActions.setCartActionsLoading(isLoading);
    }, []);

    const setCartIsFetching = useCallback((isLoading = false) => {
        cartStoreActions.setFetchingCartLoading(isLoading);
    }, []);

    const setCartSetupLoading = useCallback((isLoading = false) => {
        cartStoreActions.setCartSetupLoading(isLoading);
    }, []);

    const doResetCurrentCart = useCallback(() => {
        cartStoreActions.resetCart();
    }, []);

    const doSetCartId = useCallback((id: string) => {
        cartStoreActions.setCartId(id);
    }, []);

    // ========================================================================
    // SESSION RECONCILIATION — Sync local cartId with server session (Redis)
    // ========================================================================

    /**
     * Reconcile local cartId (Legend-State / IndexedDB) with server session (Redis).
     *
     * Cases:
     * - Session has cartId, local different → reset local, use session cartId
     * - Session empty, local has cartId → push local to session
     * - Session has cartId, local empty → use session cartId
     * - Both match or both empty → no action
     *
     * @returns The reconciled cartId (may be empty string)
     */
    const reconcileCartIdWithSession = useCallback(async (): Promise<string> => {
        if (!cartSessionManager.hasAdapter()) {
            console.log('[CartProvider] No session adapter — skipping reconciliation');
            return cartStoreActions.getCartId();
        }

        const currentStoreCode = storeCodeEmitter.getCurrentStoreCode();
        const sessionCart = await cartSessionManager.getCartId(currentStoreCode);
        const sessionCartId = sessionCart.cartId;
        const localCartId = cartStoreActions.getCartId();

        console.log('[CartProvider] Reconciling cartId with session:', {
            sessionCartId,
            localCartId,
            storeCode: currentStoreCode,
            authContextChanged: sessionCart.authContextChanged,
        });

        // AUTH CONTEXT CHANGED: Server detected auth mismatch (login/logout/session expired/different user).
        // Server already cleared session carts. We MUST clear ALL local cart data too.
        if (sessionCart.authContextChanged) {
            console.warn('[CartProvider] Auth context changed — clearing all local cart data');
            cartStoreActions.resetCart();
            cartItemReferenceStoreAPI.actions.resetStore();
            setInternalUpdate(true);
            try {
                setCartItemReferences([]);
                updateLastSyncedState([]);
            } finally {
                setInternalUpdate(false);
            }
            return '';
        }

        // Session has a cartId and it's different from local → trust session
        if (sessionCartId && sessionCartId !== localCartId) {
            console.log('[CartProvider] Session cartId wins — resetting local to session cartId');
            cartStoreActions.resetCart();
            cartItemReferenceStoreAPI.actions.resetStore();
            cartStoreActions.setCartId(sessionCartId);
            return sessionCartId;
        }

        // Session is empty but local has a cartId → push local to session
        if (!sessionCartId && localCartId) {
            console.log('[CartProvider] Pushing local cartId to session');
            await cartSessionManager.setCartId(currentStoreCode, localCartId);
            return localCartId;
        }

        // Session has cartId, local is empty → use session cartId
        if (sessionCartId && !localCartId) {
            console.log('[CartProvider] Using session cartId (local was empty)');
            cartStoreActions.setCartId(sessionCartId);
            return sessionCartId;
        }

        // Both match or both empty → no action
        return localCartId;
    }, [setCartItemReferences]);

    // ========================================================================
    // SERVER SYNC WITH CART SYNC REFERENCES API
    // ========================================================================

    /**
     * Fetch cart item references from server using cartSyncReferences API
     *
     * FLOW (Server-First):
     * 1. Fetch cartSyncReferences (server-authoritative, primary source of truth)
     * 2. If success → reconcileWithServerReferences (server-first merge)
     * 3. If empty/error → preserve local data or clear if cart is invalid
     * 4. Update reference store + draft cart for UI rendering
     *
     * CRITICAL: cartSyncReferences is the ONLY fetch source.
     */
    const fetchCartItemInternal = useCallback(
        (explicitCartId?: string): Promise<void> => {
            const currentCartId = explicitCartId || cartStoreActions.getCartId();
            console.log('[CartProvider] fetchCartItemInternal called for cartId:', currentCartId);
            if (!currentCartId) {
                cartStoreActions.resetCart();
                // Unlock sync even when no cartId — cart is genuinely empty,
                // user may add items later which should trigger sync normally.
                unlockSync();
                return Promise.resolve();
            }

            // ════════════════════════════════════════════════════════════════════
            // SYNC-IN-PROGRESS GUARD: Do not fetch server data while a sync is
            // in-flight or local state has pending changes awaiting sync.
            // ════════════════════════════════════════════════════════════════════
            // Problem: If user deletes items locally and then tab visibility changes
            // triggers fetchCartItemInternal BEFORE the deletion is synced to server,
            // reconcileWithServerReferences would see the deleted items on the server
            // and ADD THEM BACK as "new reference from server" → items reappear!
            //
            // Solution: Skip fetch when sync is active or pending. The sync mechanism
            // will ensure local and server converge. After sync completes, the next
            // fetch will get the correct server state.
            if (CartItemReferenceSynchronizationService.isInProgress()) {
                console.warn('[CartProvider] Skipping fetchCartItemInternal — sync is in progress');
                // Unlock sync — sync is already in progress, meaning it was previously
                // unlocked and actively working. Safe to keep unlocked.
                unlockSync();
                return Promise.resolve();
            }

            const syncStatus = getCartItemReferenceSyncStatus();
            if (syncStatus.hasPendingChanges) {
                console.warn(
                    '[CartProvider] Skipping fetchCartItemInternal — local has pending unsynchronized changes',
                    {
                        pendingCount: syncStatus.pendingCount,
                        lastSyncedCount: syncStatus.lastSyncedCount,
                    }
                );
                // Unlock sync — pending changes exist that need to be synced.
                // Keeping locked would prevent those changes from ever syncing.
                unlockSync();
                return Promise.resolve();
            }

            cartStoreActions.setFetchingCartLoading(true);

            // Primary: fetch cart sync references (server-authoritative)
            return fetchCartSyncReferences(currentCartId)
                .then(async syncReferencesResult => {
                    // PRIMARY PATH: sync references succeeded with data
                    if (syncReferencesResult.success && syncReferencesResult.references.length > 0) {
                        // Get current local references for any local-only state preservation
                        const localReferences: CartItemReference[] = [...cartItemReferenceStoreAPI.state.getItems()];

                        console.log('[CartProvider] Starting reconcile with data:', {
                            syncReferencesResult: syncReferencesResult,
                            localReferences: localReferences,
                            currentCartId: currentCartId,
                        });

                        // Server-first reconciliation: server data wins, preserve local-only props
                        const reconciledReferences = reconcileWithServerReferences(
                            syncReferencesResult.references,
                            localReferences,
                            currentCartId
                        );

                        // ════════════════════════════════════════════════════════════════════
                        // PENDING QUANTITY GUARD: Preserve locally-dirty quantities
                        // If user has rapid-clicked +/- buttons, there may be unsynchronized
                        // quantity changes. Don't overwrite those with stale server data.
                        // ════════════════════════════════════════════════════════════════════
                        const guardedReferences = reconciledReferences.map(ref => {
                            if (hasPendingQuantityChange(ref.cartItemReferenceId)) {
                                const localQty = getPendingQuantity(ref.cartItemReferenceId);
                                if (localQty !== undefined && localQty !== ref.qty) {
                                    console.log('[CartProvider] Preserving pending local qty for', {
                                        refId: ref.cartItemReferenceId,
                                        serverQty: ref.qty,
                                        localQty,
                                    });
                                    // Recalculate prices for the local quantity
                                    const updatedCartItem = ref.cartItem
                                        ? updateCartItemQuantityAndPrices(ref.cartItem as CartItemInterface, localQty)
                                        : ref.cartItem;
                                    return {
                                        ...ref,
                                        qty: localQty,
                                        cartItem: updatedCartItem,
                                    };
                                }
                            }
                            return ref;
                        });

                        console.log('[CartProvider] Reconciled references (server-first):', guardedReferences);

                        // CRITICAL: Wrap server-authoritative writes with setInternalUpdate
                        // to prevent triggering a spurious sync-to-server of data that
                        // just came FROM the server. Also update lastSyncedState baseline
                        // so getSyncStatus() correctly reports no pending changes.
                        setInternalUpdate(true);
                        try {
                            setCartItemReferences(guardedReferences);
                            updateLastSyncedState(guardedReferences);
                        } finally {
                            setInternalUpdate(false);
                        }

                        // Sync cartStore for backward compatibility (provider reads items/totalItems from here)
                        const cartItems = guardedReferences
                            .map(ref => ref.cartItem)
                            .filter((item): item is CartItemInterface => item != null);
                        cartStoreActions.setItems(cartItems, currentCartId);

                        setCartItemsLoaded(true);
                        return;
                    }

                    // SYNC REFERENCES EMPTY OR FAILED — no fallback to legacy getCartItems
                    console.warn(
                        '[CartProvider] Sync references empty/failed.',
                        syncReferencesResult.error ? `Error: ${syncReferencesResult.error.message}` : 'Empty result'
                    );

                    // If server explicitly returned an error indicating cart is invalid,
                    // clear local data — cart no longer exists on server.
                    if (syncReferencesResult.error) {
                        const errorMessage = syncReferencesResult.error.message || '';
                        if (
                            errorMessage === INACTIVE_CART_MESSAGE ||
                            errorMessage.includes(COULD_NOT_FIND_CART_MESSAGE)
                        ) {
                            cartStoreActions.resetCart();
                            const storeCode = storeCodeEmitter.getCurrentStoreCode();
                            cartSessionManager.clearCartId(storeCode).catch(console.error);
                            setInternalUpdate(true);
                            try {
                                setCartItemReferences([]);
                                updateLastSyncedState([]);
                            } finally {
                                setInternalUpdate(false);
                            }
                            setCartItemsLoaded(true);
                            return;
                        }
                    }

                    // Server returned empty (no error) — guard against wiping local data
                    const currentLocalReferences = cartItemReferenceStoreAPI.state.getItems();

                    if (currentLocalReferences.length > 0) {
                        console.warn(
                            '[CartProvider] Server returned empty but local has items — preserving local data.',
                            {
                                localItemCount: currentLocalReferences.length,
                                cartId: currentCartId,
                            }
                        );
                    } else {
                        // Cart is truly empty (both server and local are empty)
                        setInternalUpdate(true);
                        try {
                            setCartItemReferences([]);
                            updateLastSyncedState([]);
                        } finally {
                            setInternalUpdate(false);
                        }
                    }

                    setCartItemsLoaded(true);
                })
                .catch((error: unknown) => {
                    const errorMessage = error instanceof Error ? error.message : String(error);
                    if (errorMessage === INACTIVE_CART_MESSAGE || errorMessage.includes(COULD_NOT_FIND_CART_MESSAGE)) {
                        cartStoreActions.resetCart();
                        // Clear from session — cart no longer valid
                        const storeCode = storeCodeEmitter.getCurrentStoreCode();
                        cartSessionManager.clearCartId(storeCode).catch(console.error);
                    }
                    throw error;
                })
                .finally(() => {
                    cartStoreActions.setFetchingCartLoading(false);

                    // ════════════════════════════════════════════════════════════════════
                    // CRITICAL: Unlock sync after server data has been fetched/reconciled.
                    // Sync is locked by default on page load to prevent hydrated
                    // (potentially stale/empty) data from being pushed to server.
                    // Now that we have server-authoritative data, user-driven sync can resume.
                    // Called on both success AND error to prevent permanent lock.
                    // ════════════════════════════════════════════════════════════════════
                    unlockSync();
                });
        },
        [setCartItemReferences]
    );

    const fetchCartItem = useCallback((): Promise<void> => {
        return fetchCartItemInternal();
    }, [fetchCartItemInternal]);

    // ========================================================================
    // PROACTIVE CUSTOMER CART VALIDATION
    // ========================================================================

    /**
     * Proactively validate that the local cart ID matches the server's current
     * customer cart. Handles the cross-browser stale cart scenario:
     *
     * 1. User logs in on Browser A, adds items to cart A (saved to session)
     * 2. User opens Browser B, checks out cart A → cart A is consumed/inactive
     * 3. User returns to Browser A → local still references stale cart A
     * 4. Server rejects operations: "customer cannot perform on cart A"
     *
     * FIX: Query `customerCart` from server, compare with local cart ID.
     * If different → server's cart is the source of truth, override local.
     *
     * For guest users: skip (no customerCart query available; guest carts
     * cannot be accessed cross-browser without session sharing).
     *
     * Debounced to avoid excessive API calls on rapid tab switches.
     *
     * @returns true if cart ID was changed (caller should re-fetch items)
     */
    const validateAndReconcileCustomerCart = useCallback(async (): Promise<boolean> => {
        const currentAuthorized = authStore$.isAuthenticated.peek() ?? false;

        // Guest users — skip, no customerCart query available
        if (!currentAuthorized) {
            return false;
        }

        // Debounce: skip if validated recently
        const now = Date.now();
        if (now - lastCartValidationTime.current < CART_VALIDATION_DEBOUNCE_MS) {
            return false;
        }

        // Skip if another operation is in progress
        if (cartStoreActions.isAnyLoading() || isProcessingAuthFlow.current) {
            return false;
        }

        try {
            lastCartValidationTime.current = now;

            const result = await getCustomerCartQuery();
            const serverCartId = result?.data?.customerCart?.id;

            if (!serverCartId) {
                // Server returned no cart — customer's cart was deleted/consumed.
                // Reset local to allow a fresh cart to be created on next add-to-cart.
                console.warn('[CartProvider] validateCustomerCart: server returned no customer cart — resetting local');
                cartStoreActions.resetCart();
                cartItemReferenceStoreAPI.actions.resetStore();
                const storeCode = storeCodeEmitter.getCurrentStoreCode();
                cartSessionManager.clearCartId(storeCode).catch(console.error);
                setInternalUpdate(true);
                try {
                    setCartItemReferences([]);
                    updateLastSyncedState([]);
                } finally {
                    setInternalUpdate(false);
                }
                return true;
            }

            const localCartId = cartStoreActions.getCartId();

            if (serverCartId !== localCartId) {
                console.warn('[CartProvider] validateCustomerCart: cart ID mismatch detected', {
                    localCartId,
                    serverCartId,
                });

                // Server's customer cart is the source of truth — override local
                cartStoreActions.resetCart();
                cartItemReferenceStoreAPI.actions.resetStore();
                cartStoreActions.setCartId(serverCartId);

                // Update session to match
                const storeCode = storeCodeEmitter.getCurrentStoreCode();
                await cartSessionManager.setCartId(storeCode, serverCartId).catch(console.error);

                // Clear stale references — they belong to the old cart
                setInternalUpdate(true);
                try {
                    setCartItemReferences([]);
                    updateLastSyncedState([]);
                } finally {
                    setInternalUpdate(false);
                }

                return true;
            }

            // Cart IDs match — no action needed
            return false;
        } catch (error) {
            console.error('[CartProvider] validateCustomerCart failed — falling back to reactive handling', error);
            logSentry({
                error: error instanceof Error ? error : new Error(String(error)),
                name: 'CartProvider',
                transactionName: 'validateAndReconcileCustomerCart',
            });
            return false;
        }
    }, [getCustomerCartQuery, setCartItemReferences]);

    // ========================================================================
    // VISIBILITY CHANGE LISTENER - Handle tab visibility changes (Web adaptation)
    // ========================================================================

    const handleVisibilityChange = useCallback(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
            // Reset transient loading states
            cartStoreActions.resetTransientStates();

            // Read fresh auth state directly from store (not from stale closure)
            const currentAuthorized = authStore$.isAuthenticated.peek() ?? false;

            // Check if we should refresh cart data
            const shouldRefresh = cartStoreActions.shouldRefreshCart(currentAuthorized);

            if (shouldRefresh && !cartStoreActions.isAnyLoading()) {
                console.log('[CartProvider] Visibility change: refreshing cart data');

                if (currentAuthorized) {
                    // ════════════════════════════════════════════════════════════════
                    // AUTHENTICATED USER: Proactively validate customer cart first.
                    // This catches the cross-browser stale cart scenario where cart A
                    // was consumed in Browser B but Browser A still holds stale ID.
                    // ════════════════════════════════════════════════════════════════
                    validateAndReconcileCustomerCart()
                        .then(cartIdChanged => {
                            if (cartIdChanged) {
                                console.log(
                                    '[CartProvider] Visibility: customer cart changed — fetching new cart items'
                                );
                            }
                            // Always fetch (either new cart or refresh existing)
                            return fetchCartItemInternal();
                        })
                        .catch(error => {
                            console.error('[CartProvider] Visibility: customer cart validation failed', error);
                            // Fall back to normal fetch
                            fetchCartItemInternal();
                        });
                } else if (cartSessionManager.hasAdapter()) {
                    // Guest user with session adapter — check auth context + cross-tab drift
                    const currentStoreCode = storeCodeEmitter.getCurrentStoreCode();
                    cartSessionManager
                        .getCartId(currentStoreCode)
                        .then(sessionCart => {
                            // Auth context changed (session expired, different user)
                            // → clear ALL local cart data
                            if (sessionCart.authContextChanged) {
                                console.warn('[CartProvider] Visibility: auth context changed — clearing local cart');
                                cartStoreActions.resetCart();
                                cartItemReferenceStoreAPI.actions.resetStore();
                                return; // Don't fetch — cart is gone
                            }

                            const sessionCartId = sessionCart.cartId;
                            const localCartId = cartStoreActions.getCartId();

                            if (sessionCartId && sessionCartId !== localCartId) {
                                console.log('[CartProvider] Visibility: session cartId changed in another tab', {
                                    sessionCartId,
                                    localCartId,
                                });
                                cartStoreActions.resetCart();
                                cartItemReferenceStoreAPI.actions.resetStore();
                                cartStoreActions.setCartId(sessionCartId);
                            }

                            fetchCartItemInternal();
                        })
                        .catch(() => {
                            fetchCartItemInternal();
                        });
                } else {
                    fetchCartItemInternal();
                }
            }
        }
    }, [fetchCartItemInternal, validateAndReconcileCustomerCart]);

    useEffect(() => {
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', handleVisibilityChange);

            return () => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
            };
        }
    }, [handleVisibilityChange]);

    // ========================================================================
    // EXISTING BUSINESS LOGIC - Keep unchanged
    // ========================================================================

    const addItemToCart = useCallback((item: CartItemInterface, quantity: number) => {
        cartActionsEmitter.addItem(item.product.id, quantity);
        cartStoreActions.addItemWithQuantity(item, quantity);
    }, []);

    const handleMergeCart = useCallback(
        async (
            localCartId: string,
            authenticatedCartId: string
        ): Promise<{ success: boolean; resolvedCartId: string }> => {
            // Case 1: No local cart — just use customer cart
            if (!localCartId && authenticatedCartId) {
                cartStoreActions.setCartId(authenticatedCartId);
                return { success: true, resolvedCartId: authenticatedCartId };
            }

            // Case 2: No carts at all — reset
            if (!localCartId && !authenticatedCartId) {
                cartStoreActions.resetCart();
                return { success: true, resolvedCartId: '' };
            }

            // Case 3: Same cart or no local — no merge needed
            if (!localCartId || localCartId === authenticatedCartId) {
                return { success: true, resolvedCartId: authenticatedCartId };
            }

            // Case 3.5: Local cart has no items — skip assign, just use customer cart.
            // This avoids a failed assignCustomerToGuestCart call when the local cart is stale/inactive
            // (e.g., user completed checkout in another browser, then fresh-loaded this one).
            const localItems = cartStoreActions.getCartState().items;
            const localReferences = cartItemReferenceStoreAPI.state.getItems();
            if (localItems.length === 0 && localReferences.length === 0) {
                console.log('[CartProvider] Local cart is empty — skipping assign, using customer cart directly');
                cartStoreActions.resetCart();
                cartItemReferenceStoreAPI.actions.resetStore();
                cartStoreActions.setCartId(authenticatedCartId);
                // Update session
                const storeCode = storeCodeEmitter.getCurrentStoreCode();
                cartSessionManager.setCartId(storeCode, authenticatedCartId).catch(console.error);
                return { success: true, resolvedCartId: authenticatedCartId };
            }

            // Case 4: Both exist, different IDs — assign customer to guest cart.
            // Instead of merging two carts, we assign the authenticated customer
            // to the existing guest cart. The guest cart becomes the customer's cart,
            // preserving the same cart ID and all items/rules.
            // If assignment fails, fallback to reference-based merge (current behavior).
            try {
                console.log('[CartProvider] Assigning customer to guest cart:', {
                    guestCartId: localCartId,
                    previousCustomerCartId: authenticatedCartId,
                });

                const assignedCartId = await assignCustomerToGuestCart(localCartId);
                const finalCartId = assignedCartId ?? localCartId;

                console.log('[CartProvider] Customer assigned to guest cart successfully, cart ID:', finalCartId);
                cartStoreActions.setCartId(finalCartId);
                return { success: true, resolvedCartId: finalCartId };
            } catch (assignError) {
                // FALLBACK: Assignment failed — use reference-based merge.
                // Local refs are preserved locally (their cartId ≠ authenticatedCartId).
                // When fetchCartItemInternal runs, reconcileWithServerReferences will keep them
                // as "pending additions". forceCartItemReferenceSync will push them to server.
                console.warn(
                    '[CartProvider] assignCustomerToGuestCart failed, falling back to reference-based merge:',
                    assignError
                );
                logSentry({
                    error: assignError instanceof Error ? assignError : new Error(String(assignError)),
                    name: 'CartProvider',
                    transactionName: 'handleMergeCart.assignCustomerToGuestCart',
                    extra: {
                        localCartId,
                        authenticatedCartId,
                        message: assignError instanceof Error ? assignError.message : String(assignError),
                    },
                });

                // Do NOT clear local references — they are needed for reference-based fallback merge.
                cartStoreActions.setCartId(authenticatedCartId);
                return { success: true, resolvedCartId: authenticatedCartId };
            }
        },
        [assignCustomerToGuestCart]
    );

    // ========================================================================
    // AUTHENTICATION FLOW - Race condition protected
    // FIXED: Removed handleAfterMergingCart from dependencies to prevent infinite loop
    // ========================================================================

    useEffect(() => {
        console.log(
            '[CartProvider] Auth state or cartId changed. Authorized',
            authorized,
            'CartId:',
            cartId,
            'CustomerCartLoaded:',
            customerCartLoaded,
            'AnyLoading:',
            cartStoreActions.isAnyLoading()
        );

        // Wait for session to resolve before deciding auth vs guest flow
        if (sessionStatus === 'loading') {
            return;
        }

        // Prevent race condition - exit if already processing
        if (isProcessingAuthFlow.current) {
            return;
        }

        // Wait for session reconciliation to complete before proceeding
        if (!isSessionReconciled.current) {
            return;
        }

        if (authorized && !customerCartLoaded && !cartStoreActions.isAnyLoading()) {
            // Set flag to prevent concurrent executions
            isProcessingAuthFlow.current = true;
            cartStoreActions.setCartActionsLoading(true);

            // Wait for reconciliation promise (should already be resolved)
            const startAuthFlow = async () => {
                if (reconciliationPromise.current) {
                    await reconciliationPromise.current;
                }

                getCustomerCartQuery()
                    .then(async result => {
                        if (result?.data?.customerCart?.id) {
                            const mergeResult = await handleMergeCart(cartId, result?.data?.customerCart?.id);

                            // Inline handleAfterMergingCart logic to avoid dependency loop
                            if (mergeResult.success) {
                                setCustomerCartLoaded(true);

                                // Use the resolved cart ID directly from handleMergeCart
                                // instead of reading from store (Legend-State may not have propagated yet).
                                const activeCartId = mergeResult.resolvedCartId;

                                // Persist active cart ID to session
                                const storeCode = storeCodeEmitter.getCurrentStoreCode();
                                if (activeCartId) {
                                    cartSessionManager.setCartId(storeCode, activeCartId).catch(console.error);
                                }

                                // Fetch cart items after successful merge to sync with server
                                try {
                                    await fetchCartItemInternal(activeCartId);

                                    // Mark cart as initialized BEFORE forceSync (which is fire-and-forget).
                                    // This allows downstream consumers (CartScreen) to proceed.
                                    setCartInitialized(true);

                                    // After fetch + reconciliation, guest references are preserved locally
                                    // (their cartId ≠ customerCartId, so reconcileWithServerReferences keeps them).
                                    // Force a sync to push the merged set (customer + guest refs)
                                    // to the server via synchronizeCart mutation.
                                    // This triggers CartReconciliationProcessor to add guest items
                                    // to the customer's Magento cart and AuditDataPersistenceProcessor
                                    // to save all refs with customer_id.
                                    await forceCartItemReferenceSync(activeCartId);
                                } catch {
                                    // Error is intentionally ignored - cart will be refreshed on next attempt
                                    // CRITICAL: Still mark as initialized so CartScreen is not stuck
                                    setCartInitialized(true);
                                } finally {
                                    cartStoreActions.setCartActionsLoading(false);
                                }
                            } else {
                                cartStoreActions.setCartActionsLoading(false);
                                setCartInitialized(true);
                                unlockSync();
                            }
                        } else {
                            // rarely happens, but if it does, reset the cart
                            cartStoreActions.resetCart();
                            // Clear from session when cart is invalid
                            const storeCode = storeCodeEmitter.getCurrentStoreCode();
                            cartSessionManager.clearCartId(storeCode).catch(console.error);
                            cartStoreActions.setCartActionsLoading(false);
                            setCartInitialized(true);
                            unlockSync();
                            // We will assign the guest cart to the user without merging - waiting for backend support
                        }
                    })
                    .catch((error: unknown) => {
                        const errorMessage = error instanceof Error ? error.message : String(error);
                        if (
                            errorMessage === INACTIVE_CART_MESSAGE ||
                            errorMessage.includes(COULD_NOT_FIND_CART_MESSAGE)
                        ) {
                            cartStoreActions.resetCart();
                            // Clear from session on inactive/not-found cart
                            const storeCode = storeCodeEmitter.getCurrentStoreCode();
                            cartSessionManager.clearCartId(storeCode).catch(console.error);
                        }
                        cartStoreActions.setCartActionsLoading(false);
                        // CRITICAL: Mark customerCartLoaded to prevent infinite retry loop.
                        // Without this, the auth effect condition (authorized && !customerCartLoaded && !isAnyLoading)
                        // remains true after isProcessingAuthFlow resets in finally → infinite loop.
                        setCustomerCartLoaded(true);
                        // CRITICAL: Always mark as initialized on error so CartScreen is not stuck
                        setCartInitialized(true);
                        unlockSync();
                    })
                    .finally(() => {
                        // Always reset the flag in finally block to ensure it's reset even if errors occur
                        isProcessingAuthFlow.current = false;
                    });
            };

            startAuthFlow().catch(console.error);
        } else if (!authorized && !cartStoreActions.isAnyLoading() && !cartInitialized) {
            // Prevent race condition for fetch cart items flow
            if (isFetchingCartItems.current) {
                return;
            }

            // Set flag to prevent concurrent fetch operations
            isFetchingCartItems.current = true;
            console.log('Starting fetch cart items for unauthorized user');

            // Wait for reconciliation then fetch
            // IMPORTANT: Guest users may still have a cart ID persisted in cookie/session
            // from a previous session. reconcileCartIdWithSession already handles syncing
            // the session ↔ local cart ID (using the correct store code from the cookie).
            // We use the reconciled result directly to avoid store code mismatch.
            const startGuestFlow = async () => {
                if (reconciliationPromise.current) {
                    // reconcileCartIdWithSession returns the authoritative cartId
                    // and already persists to session with the correct store code.
                    const reconciledCartId = await reconciliationPromise.current;

                    // If reconciliation produced a different cartId than what's in store,
                    // ensure the store is updated (handles edge case where reconciliation
                    // resolved before the store observable propagated).
                    const currentStoreCartId = cartStoreActions.getCartId();
                    if (reconciledCartId && reconciledCartId !== currentStoreCartId) {
                        console.log('[CartProvider] Guest flow: using reconciled cartId over store cartId', {
                            reconciledCartId,
                            currentStoreCartId,
                        });
                        cartStoreActions.setCartId(reconciledCartId);
                    }
                }

                // Track which cartId we're fetching with
                lastFetchedCartIdRef.current = cartStoreActions.getCartId();
                return fetchCartItemInternal();
            };

            startGuestFlow()
                .catch(error => {
                    console.error('Error fetching cart items for unauthorized user:', error);
                })
                .finally(() => {
                    // Always reset the flag after fetch completes
                    isFetchingCartItems.current = false;
                    // Mark cart as initialized for guest flow
                    setCartInitialized(true);
                    console.log('Fetch cart items completed, flag reset');

                    // ════════════════════════════════════════════════════════════════════
                    // RACE CONDITION FIX: IndexedDB hydration may have changed cartId
                    // while the above fetch was in-flight. The auth effect re-run was
                    // blocked by isFetchingCartItems guard, so we need to re-fetch now
                    // with the updated cartId.
                    // ════════════════════════════════════════════════════════════════════
                    const currentCartId = cartStoreActions.getCartId();
                    if (currentCartId && currentCartId !== lastFetchedCartIdRef.current) {
                        console.log('[CartProvider] CartId changed during fetch (IndexedDB hydration), re-fetching', {
                            previousCartId: lastFetchedCartIdRef.current,
                            currentCartId,
                        });
                        lastFetchedCartIdRef.current = currentCartId;
                        fetchCartItemInternal().catch(error => {
                            console.error('[CartProvider] Re-fetch after cartId change failed:', error);
                        });
                    } else {
                        unlockSync();
                    }
                });
        } else {
            // ════════════════════════════════════════════════════════════════════
            // CATCH-ALL: Effect re-ran but neither auth flow nor guest flow executed.
            // This happens when:
            // - authorized && customerCartLoaded (auth flow already completed)
            // - authorized && isAnyLoading() (loading in progress)
            // - !authorized && cartInitialized (guest flow already completed)
            // - !authorized && isAnyLoading() (loading in progress)
            //
            // If cart is already initialized, server data was fetched in a previous
            // cycle → safe to unlock sync so user changes can be pushed to server.
            // ════════════════════════════════════════════════════════════════════
            unlockSync();
        }
    }, [
        authorized,
        sessionStatus,
        cartId,
        customerCartLoaded,
        getCustomerCartQuery,
        handleMergeCart,
        fetchCartItemInternal,
        reconcileCartIdWithSession,
        setCustomerCartLoaded,
        setCartInitialized,
        cartInitialized,
        // REMOVED: handleAfterMergingCart - inlined the logic to prevent infinite loop
    ]);

    // ========================================================================
    // ITEM ACTIONS - Simple, focused functions
    // ========================================================================

    const addItemsToCart = useCallback((items: CartItemInterface[]) => {
        cartStoreActions.addItemsWithQuantity(items);
    }, []);

    const removeItemFromCart = useCallback((id: CartItemInterface['id']) => {
        cartStoreActions.removeItemOrQuantity(id);
    }, []);

    const updateItemInCart = useCallback((id: CartItemInterface['id'], quantity: number) => {
        cartStoreActions.updateItemWithQuantity(id, quantity);
    }, []);

    const clearItemFromCart = useCallback((id: CartItemInterface['id']) => {
        cartActionsEmitter.removeItem(id);
        cartStoreActions.removeItem(id);
    }, []);

    const updateCartLanguage = useCallback((language: string) => {
        cartStoreActions.updateCartLanguage(language);
    }, []);

    const setCartItems = useCallback((items: CartItemInterface[]) => {
        cartStoreActions.setItems(items, cartStoreActions.getCartId());
    }, []);

    // ========================================================================
    // COMPUTED VALUES - Clean computed layer
    // ========================================================================

    // IMPORTANT: Do NOT wrap in useMemo(fn, []) — these values use .get() internally
    // which creates reactive subscriptions in the observer(). Caching in useMemo(fn, [])
    // prevents re-execution on re-render, causing the observer to lose subscriptions
    // and computed values to become stale.
    // NOTE: Use cartComputed (with .get()) for ALL values to ensure proper reactive tracking.

    // ========================================================================
    // APP INITIALIZATION - Reset transient states on app start
    // ========================================================================

    useEffect(() => {
        if (!isInitialized.current) {
            console.log('[CartProvider] First mount - initializing cart store');
            cartStoreActions.initializeCartStore();
            isInitialized.current = true;
        }
        if (!isSessionReconciled.current) {
            console.log('[CartProvider] Starting session reconciliation (first mount or store code change)');
            isSessionReconciled.current = true;
            reconciliationPromise.current = reconcileCartIdWithSession().catch(error => {
                console.error('[CartProvider] Session reconciliation failed:', error);
                return cartStoreActions.getCartId();
            });
        }
    }, [reconcileCartIdWithSession, cartInitialized]);

    // ========================================================================
    // PROVIDER VALUE - Clean construction with new CartState structure
    // ========================================================================

    const value: CartProviderState = useMemo(() => {
        const computedValues = {
            totalItems: cartComputed.getTotalItems(),
            totalUniqueItems: cartComputed.getTotalUniqueItems(),
            totalValue: cartComputed.getTotalValue(),
            isEmpty: cartComputed.isEmpty(),
            isAnyLoading: cartComputed.isAnyLoading(),
            isCartActionsLoading: cartComputed.isCartActionsLoading(),
            isFetchingCart: cartComputed.isFetchingCart(),
            isCartSetupLoading: cartComputed.isCartSetupLoading(),
        };

        return {
            cartId,
            // === CART STATE (Server data + convenience properties) ===
            cartData: {
                // Server Cart data (with proper snake_case from GraphQL)
                id: cartState.id,
                is_virtual: cartState.isVirtual,
                shipping_addresses: cartState.shippingAddresses || [],
                total_quantity: cartState.totalQuantity,
                total: cartState.total,
                items: cartState.items,
                __typename: 'Cart',
            },

            // Convenience properties (computed from cartData)
            items: cartState.items,
            totalItems: computedValues.totalItems,

            // Local loading states
            isCartLoading: computedValues.isCartActionsLoading,
            isFetchingCart: computedValues.isFetchingCart,

            // === CONVENIENCE METHODS ===
            doResetCurrentCart,
            getIdFromCart: () => cartState.cartId,
            setCartLoading,
            addOrIncreaseItemToCart: addItemToCart,
            decreaseItemInCart: removeItemFromCart,
            getItemFromCart: (item: CartItemInterface | string) => {
                if (typeof item === 'string') {
                    return cartState.items.find((i: CartItemInterface) => i.id === item);
                }
                return getItemByProduct(cartState.items, item);
            },
            isInCart: (item: CartItemInterface | string) => {
                if (typeof item === 'string') {
                    return cartState.items.some((i: CartItemInterface) => i.id === item);
                }
                return !!getItemByProduct(cartState.items, item);
            },
            doSetCartId,
            setCartItems,
            cartTotal: () => cartState.total,
            cartTotalItems: () => computedValues.totalItems,
            clearItemFromCart,
            setCartIsFetching,
            fetchCartItem,
            updateItemInCart,
            quantitiesOfProductInCart: (productId: number) => {
                const cartItem = cartState.items.find((item: CartItemInterface) => item.product.id === productId);
                return cartItem ? cartItem.quantity : 0;
            },
            getCartItemBySku: (sku: string) => {
                return cartState.items.find((item: CartItemInterface) => item.product.sku === sku);
            },

            // === COMPUTED METHODS ===
            getTotalUniqueItems: () => computedValues.totalUniqueItems,
            getTotalValue: () => computedValues.totalValue,
            isCartEmpty: () => computedValues.isEmpty,
            isAnyLoading: () => computedValues.isAnyLoading,
            isCartActionsLoading: () => computedValues.isCartActionsLoading,
            isCartFetchingLoading: () => computedValues.isFetchingCart,
            isCartSetupLoading: () => computedValues.isCartSetupLoading,
            setCartSetupLoading,

            // === MISSING METHODS - Now exposed ===
            addItemsToCart,
            updateCartLanguage,

            // === CART INITIALIZATION STATE ===
            isCartInitialized: () => cartInitialized,

            // === PROACTIVE CART VALIDATION ===
            validateCartBeforeOperation: async () => {
                const cartIdChanged = await validateAndReconcileCustomerCart();
                if (cartIdChanged) {
                    await fetchCartItemInternal();
                }
            },

            // === LEGACY SUPPORT ===
            createEmptyCart: createEmptyCartMutation,
        };
    }, [
        cartId,
        cartState,
        doResetCurrentCart,
        setCartLoading,
        doSetCartId,
        setCartIsFetching,
        setCartSetupLoading,
        createEmptyCartMutation,
        addItemToCart,
        removeItemFromCart,
        clearItemFromCart,
        fetchCartItem,
        updateItemInCart,
        setCartItems,
        addItemsToCart,
        updateCartLanguage,
        cartInitialized,
        validateAndReconcileCustomerCart,
        fetchCartItemInternal,
    ]);

    return <WebCartContext.Provider value={value}>{children}</WebCartContext.Provider>;
});
